import React, { useState } from 'react';
import type Lesson from '@whizz/api-client/dist/@types/model/Lesson';
import type { ToggleModalProps } from '../../../components/modal/types';
import type { AxiosError } from 'axios';
import type { StudentType } from '../../types';
import { model } from '@whizz/api-client';
import { useI18n } from '@whizz/react-i18n';
import { Textarea } from '../../../components';
import { CancelButtonModal, FormGenericError, SaveButton } from '../../../global';
import { useMutation } from 'react-query';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { ModalFooter } from '../../../components/modal/Modal';
import { GenericModal } from '../../../hooks/useSuccessErrorModal';
import { GLOBAL_ICONS } from '../../../util/vars';

export interface ModalProps extends Pick<ToggleModalProps, 'toggle'> {
  student: StudentType;
  lesson: Lesson,
  onClose?: () => void;
}

interface FormData {
  reason: string;
}

const VALIDATION_SCHEMA = Yup.object().shape({
  reason: Yup.string(),
});

const SkipExerciseModal = ({ student, lesson, toggle, onClose }: ModalProps): JSX.Element => {
  const { translate } = useI18n();
  const [ status, setStatus ] = useState<{ status: 'idle' | 'success' | 'error', message: string }>({ status: 'idle', message: '' });

  const mutations = {
    skip: useMutation(
      () => model.Lesson.skip(student.id),
      {
        onSuccess: () => {
          setStatus({ status: 'success', message: translate('pages.HOME.CURRENT_LESSON.MODAL.SUCCESS') });
        },
        onError: (error: AxiosError<{ error: string }>) => {
          if (null == error.response) {
            return;
          }
          setStatus({ status: 'error', message: translate('pages.HOME.CURRENT_LESSON.MODAL.ERROR') });
        },
      },
    ),
    reason: useMutation(
      ({ formData }: { formData: FormData }) => new model.LessonFeedback({ lesson, description: formData.reason, student_id: student.id }).create(),
    ),
  };

  const handleSubmit = (formData: FormData) => {
    mutations.skip.mutate();
    if (formData.reason) {
      mutations.reason.mutate({ formData });
    }
  };

  const handleClose = () => {
    setStatus({ status: 'idle', message: '' });
    onClose?.();
    toggle(false);
  };

  if ([ 'success', 'error' ].includes(status.status)) {
    return (
      <GenericModal open toggle={handleClose}>
        {status.message}
      </GenericModal>
    );
  }

  return (
    <Formik<FormData>
      initialValues={{
        reason: '',
      }}
      validationSchema={VALIDATION_SCHEMA}
      onSubmit={(formData) => handleSubmit(formData)}
    >
      {({ handleSubmit, getFieldProps, errors, touched, isValid }) => (
        <form onSubmit={handleSubmit}>
          <div className={'container space-y-6'}>
            <h3 className={'font-bold'}>{translate('pages.HOME.CURRENT_LESSON.MODAL.REASON')}</h3>
            <Textarea
              {...getFieldProps('reason')}
              id={'reason'}
              label={''}
              hideLabel
              error={touched.reason ? errors.reason : null}
            />

            {(!isValid && Object.keys(errors).some((key) => touched[key as keyof FormData]))
            && (<FormGenericError />)}
          </div>

          <ModalFooter justify={'end'}>
            <CancelButtonModal
              type={'button'}
              className={'mr-5'}
              onClick={handleClose}
            >
              {translate('components.BUTTONS.CANCEL')}
            </CancelButtonModal>
            <SaveButton
              type={'submit'}
              showConfirmLabel
              icon={GLOBAL_ICONS.checkbox}
              isLoading={mutations.skip.isLoading}
            />
          </ModalFooter>
        </form>
      )}
    </Formik>
  );
};

export default SkipExerciseModal;
